// The Google dashboard's DIMENSIONAL widget data — PURE.
//
// The dimensional widgets (pie, ranked bar) split one metric by one dimension.
// ⚠️ BOTH DIMENSIONS COME FROM DATA THE PAGE ALREADY HAS ON SCREEN — the
// campaign-type breakdown and the explorer tree — so adding a dimensional widget
// costs no extra query. That constraint is why the offered list is exactly two:
// brand / AM / group are FILTERS on this dashboard, not groupings, and offering
// them would mean a new server aggregation, which this phase deliberately does
// not build.
//
// ⚠️⚠️ THE MIXED-CURRENCY RULE LIVES HERE, at the point where per-currency
// amounts are collapsed into one plottable number — and it is now CONDITIONAL on
// the agency display currency:
//
//   · NATIVE mode — unchanged. `metricMoney` returns an amount PER CURRENCY;
//     summing across them would invent a figure that exists in no currency, and a
//     pie is precisely the shape that hides it (the slices still total a tidy
//     100%). So a money metric in a multi-currency scope reports `mixedCurrency`
//     and the widget refuses to plot — see widgetSlices' canPlotDimensional.
//   · DISPLAY-CURRENCY mode — each entity's metric block is CONVERTED first
//     (convertGoogleMetrics), so every entity is single-currency by construction
//     and the widget plots the rated blend.
//
// ⚠️ The refusal moved behind a condition; it did not go away. A RAW sum stays
// impossible in both modes. Counts and percentages are currency-agnostic and pass
// straight through either way.
//
// ⚠️ THE `ReportingWidgets` IMPORT IS TYPE-ONLY, and must stay that way. This
// module is reached from the PREFS module (which passes its dimension predicate
// to the widget decoder), so a runtime import of the component layer here would
// re-create exactly the circular-init edge googleAdsChartCatalog.ts documents —
// the one that white-screened the app. `import type` is erased, so there is no
// runtime edge at all.

import {
  isMoneyMetric,
  metricDef,
  metricMoney,
  metricScalar,
} from '../../lib/googleAdsMetrics';
import type { TrendMetricFormat } from '../reporting/trendSeries';
import type { WidgetDimensionalData, WidgetSlice } from '../reporting/ReportingWidgets';
import type {
  GoogleAdsChannelBreakdownRow,
  GoogleAdsMetricKey,
  GoogleAdsMetrics,
  GoogleAdsTreeResponse,
} from '../../types/googleAdsReporting';
import { formatEnum } from './metricFormat';
import { convertGoogleMetrics } from './googleCurrency';
import type { GoogleMoneyDisplay } from './googleTrendSeries';

export const GOOGLE_DIMENSION_CAMPAIGN_TYPE = 'channelType';
export const GOOGLE_DIMENSION_LOCATION = 'location';

export const GOOGLE_WIDGET_DIMENSIONS = [
  { key: GOOGLE_DIMENSION_CAMPAIGN_TYPE, label: 'Campaign type' },
  { key: GOOGLE_DIMENSION_LOCATION, label: 'Location' },
] as const;

export function isGoogleWidgetDimension(key: string): boolean {
  return GOOGLE_WIDGET_DIMENSIONS.some((d) => d.key === key);
}

/** How the widget should format this metric's values. */
function formatOf(key: GoogleAdsMetricKey): TrendMetricFormat {
  const kind = metricDef(key)?.kind;
  if (kind === 'money') return 'money';
  if (kind === 'pct') return 'ratio';
  return 'count';
}

/**
 * One entity's value for a metric, plus the currency it is in.
 *
 * ⚠️ A MONEY metric returns the amount ONLY when that entity has exactly one
 * currency. More than one and there is no single figure to plot for it, so it
 * reports as unmeasured rather than as a sum.
 */
function valueOf(
  metrics: GoogleAdsMetrics,
  key: GoogleAdsMetricKey,
): { value: number | null; currency: string | null } {
  if (isMoneyMetric(key)) {
    const amounts = metricMoney(metrics, key);
    if (amounts.length !== 1) return { value: null, currency: amounts[0]?.currency ?? null };
    return { value: amounts[0]!.amount, currency: amounts[0]!.currency };
  }
  return { value: metricScalar(metrics, key), currency: null };
}

function build(
  key: GoogleAdsMetricKey,
  entries: Array<{ key: string; label: string; metrics: GoogleAdsMetrics }>,
  loading: boolean,
  unavailable?: string,
  money?: GoogleMoneyDisplay,
): WidgetDimensionalData {
  const format = formatOf(key);
  const slices: WidgetSlice[] = [];
  const currencies = new Set<string>();
  const converting = Boolean(money && money.display && money.display !== 'native');

  for (const entry of entries) {
    // Converting the entity's whole metric BLOCK keeps a money ratio correct —
    // it is re-derived from that entity's converted total, not converted after
    // being divided.
    const metrics = converting
      ? (convertGoogleMetrics(entry.metrics, money!.display, money!.snapshot) ?? entry.metrics)
      : entry.metrics;
    const { value, currency } = valueOf(metrics, key);
    if (currency) currencies.add(currency);
    // A money entity spanning several currencies also contributes all of them,
    // so the mixed flag below catches it even though its own value read null.
    if (isMoneyMetric(key)) {
      for (const amount of metricMoney(entry.metrics, key)) currencies.add(amount.currency);
    }
    slices.push({ key: entry.key, label: entry.label, value });
  }

  const list = [...currencies].sort();
  return {
    slices,
    currency: converting ? money!.display : list.length === 1 ? list[0]! : null,
    // ⚠️ Only in NATIVE mode — with a display currency every entity above was
    // converted, so there genuinely is one currency and refusing would withhold a
    // figure we can legitimately produce.
    mixedCurrency: !converting && format === 'money' && list.length > 1,
    currencies: list,
    format,
    unavailable,
    loading,
  };
}

/**
 * Split a metric by campaign type or by Location, from what the page already
 * fetched.
 *
 * ⚠️ THE LOCATION DIMENSION DEPENDS ON THE TREE'S GROUPING. With Location turned
 * off in the level chips the tree's shallowest rows are campaigns, not
 * locations — so the widget SAYS the dimension is unavailable and how to get it
 * back, rather than quietly plotting campaigns under a "by Location" title.
 */
export function buildGoogleDimensionalData(input: {
  metricKey: GoogleAdsMetricKey;
  dimension: string;
  channels: readonly GoogleAdsChannelBreakdownRow[] | undefined;
  channelsLoading: boolean;
  tree: GoogleAdsTreeResponse | undefined;
  treeLoading: boolean;
  /** The agency display currency. Absent = native (the refusal stands). */
  money?: GoogleMoneyDisplay;
}): WidgetDimensionalData {
  const { metricKey, dimension, money } = input;

  if (dimension === GOOGLE_DIMENSION_CAMPAIGN_TYPE) {
    const rows = input.channels ?? [];
    return build(
      metricKey,
      rows.map((r) => ({
        key: r.channelType,
        label: formatEnum(r.channelType),
        metrics: r.metrics,
      })),
      input.channelsLoading,
      undefined,
      money,
    );
  }

  if (dimension === GOOGLE_DIMENSION_LOCATION) {
    const tree = input.tree;
    if (tree && tree.levels[0] !== 'location') {
      return build(
        metricKey,
        [],
        input.treeLoading,
        'Turn the Location level on to split by it.',
        money,
      );
    }
    const rows = (tree?.rows ?? []).filter((r) => r.level === 'location' && r.depth === 0);
    return build(
      metricKey,
      rows.map((r) => ({ key: r.id, label: r.name, metrics: r.metrics })),
      input.treeLoading,
      undefined,
      money,
    );
  }

  return build(metricKey, [], false, 'That dimension isn’t available on this dashboard.', money);
}
